import React, { useContext, useEffect, useState } from "react";
import { TabKeys } from "../../data/tabs";
import { renderer } from "../../three/global-renderer";

interface MenuValue {
  isMobile: boolean;
  setIsMobile: (isMobile: boolean) => void;
  selectedTab: TabKeys;
  onTabSelected: (tab: TabKeys) => void;
  dropDownOpenTitle: string;
  onDropDownToggled: (title: string) => void;
  isHamburgerOpen: boolean;
  onHamburgerToggled: () => void;
}

const MenuContext = React.createContext<MenuValue>({
  isMobile: false,
  setIsMobile: () => {
    void 0;
  },
  selectedTab: TabKeys.Home,
  onTabSelected: () => {
    void 0;
  },
  dropDownOpenTitle: "",
  onDropDownToggled: () => {
    void 0;
  },
  isHamburgerOpen: false,
  onHamburgerToggled: () => {
    void 0;
  },
});

export const useMenu = (): MenuValue => useContext(MenuContext);

type MenuProviderProps = {
  children?: React.ReactNode;
};

const getTabFromHash = (): TabKeys => {
  const hash = window.location.hash.replace("#", "");
  switch (hash) {
    case "about":
      return TabKeys.About;
    case "services":
      return TabKeys.Services;
    case "contact":
      return TabKeys.Contact;
    case "privacy-policy":
      return TabKeys.PrivacyPolicy;
    case "terms-and-conditions":
      return TabKeys.TermsAndConditions;
    case "home":
    default:
      return TabKeys.Home;
  }
};

const getHashFromTab = (tab: TabKeys): string => {
  switch (tab) {
    case TabKeys.About:
      return "about";
    case TabKeys.Services:
      return "services";
    case TabKeys.Contact:
      return "contact";
    case TabKeys.PrivacyPolicy:
      return "privacy-policy";
    case TabKeys.TermsAndConditions:
      return "terms-and-conditions";
    case TabKeys.Home:
    default:
      return "home";
  }
};

export const MenuProvider: React.FC<MenuProviderProps> = (
  props: MenuProviderProps,
) => {
  const [selectedTab, setSelectedTab] = useState<TabKeys>(getTabFromHash());
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [dropDownOpenTitle, setDropDownOpenTitle] = useState<string>("");
  const [isHamburgerOpen, setIsHamburgerOpen] = useState<boolean>(false);

  const onDropDownToggled = (id: string): void => {
    setDropDownOpenTitle(id);
  };

  const onHamburgerToggled = (): void => {
    const newState = !isHamburgerOpen;
    setIsHamburgerOpen(newState);
    renderer.setMobileMenuOpen(newState);
  };

  const onTabSelected = (tab: TabKeys): void => {
    setIsHamburgerOpen(false);
    renderer.setMobileMenuOpen(false);
    if (dropDownOpenTitle !== "") {
      setDropDownOpenTitle("");
    } else {
      setIsHamburgerOpen(false);
    }

    setSelectedTab(tab);
    window.location.hash = getHashFromTab(tab);
  };

  useEffect(() => {
    renderer.setTab(selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    const handleHashChange = () => {
      const newTab = getTabFromHash();
      if (newTab !== selectedTab) {
        setSelectedTab(newTab);
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [selectedTab]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 992) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    // Initial check
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setIsMobile]);

  return (
    <MenuContext.Provider
      value={{
        isMobile,
        setIsMobile,
        selectedTab,
        onTabSelected,
        dropDownOpenTitle,
        onDropDownToggled,
        isHamburgerOpen,
        onHamburgerToggled,
      }}>
      {props.children}
    </MenuContext.Provider>
  );
};
